import React, {useState, useEffect, useContext} from 'react';
import {NotificationContext} from '../../context/NotificationProvider';
import {VendorContext} from '../../context/VendorProvider';
import {WebsiteContext} from '../../context/WebsiteProvider';
import VendorModal from '../../components/modals/VendorModal';
import VendorForm from '../vendor/VendorForm';
import VendorFiles from '../vendor/VendorFiles';
import Draggable from 'react-draggable';
import WebsiteModal from '../../components/modals/WebsiteModal';
import WebsiteForm from '../website/WebsiteForm';

const categories = [
  {id: 1, name: 'Comments', category: 'Comments'},
  {id: 2, name: 'Files', category: 'Files'},
  {id: 3, name: 'Websites', category: 'Websites'},
  {id: 4, name: 'Contacts', category: 'Contacts'},
];

export default function NotificationsPage() {
  const {
    getAllNotifications,
    deleteNotification,
    dismissSelectedNotifications,
    refreshKey,
  } = useContext(NotificationContext);
  const {getWebsite} = useContext(WebsiteContext);
  const {getVendor, isOpen, setIsOpen, selectedVendor, setSelectedVendor} = useContext(VendorContext);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [selectedAccountType, setSelectedAccountType] = useState('');
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [selectedWebsite, setSelectedWebsite] = useState(null);
  const [websiteIsOpen, setWebsiteIsOpen] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showEmptyMessage, setShowEmptyMessage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 500;
  const totalPages = Math.ceil(filteredNotifications.length / itemsPerPage);
  const maxPagesToShow = 20;

  const vendorName = selectedVendor ? selectedVendor.name : '';
  const websiteName = selectedWebsite ? selectedWebsite.name : '';

  const handleFilter = (category) => {
    setSelectedAccountType(category);
    setCurrentPage(1);
  };

  const closeVendorModal = () => setIsOpen(false);
  const closeWebsiteModal = () => setWebsiteIsOpen(false);

  const handleSelectAll = () => {
    const checked = !selectAll;
    setSelectAll(checked);
    const currentItems = paginatedNotifications.map((n) => n.rowKey);
    setSelectedNotifications(checked ? currentItems : []);
  };

  const handleNotificationSelection = (rowKey, e) => {
    const checked = e.target.checked;
    setSelectedNotifications((prevSelected) =>
      checked ? [...prevSelected, rowKey] : prevSelected.filter((item) => item !== rowKey)
    );
  };

  const dismissSelected = () => {
    dismissSelectedNotifications(selectedNotifications);
    setSelectedNotifications([]);
    setSelectAll(false);
  };

  const dismissNotification = (rowKey) => deleteNotification(notifications.find((n) => n.rowKey === rowKey));

  const viewNotification = async (link) => {
    const vendorIdMatch = link.match(/vendorId=(\d+)|vendor=(\d+)/);
    const websiteIdMatch = link.match(/site=(\d+)/);

    if (websiteIdMatch) {
      const websiteId = websiteIdMatch[1];
      const websiteData = await getWebsite(websiteId);
      if (websiteData) {
        setSelectedWebsite(websiteData);
        setSelectedVendor(null);
        setWebsiteIsOpen(true);
        setIsOpen(false);
      }
    } else if (vendorIdMatch) {
      const vendorId = vendorIdMatch[1] || vendorIdMatch[2];
      const vendorData = await getVendor(vendorId);
      if (vendorData) {
        setSelectedVendor(vendorData);
        setSelectedWebsite(null);
        setIsOpen(true);
        setWebsiteIsOpen(false);
      }
    }
  };

  const refresh = async () => {
    const newNotifications = await getAllNotifications();
    setNotifications(newNotifications);
  };

  function capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  function formatTimestamp(isoString) {
    return new Date(isoString).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }

  useEffect(() => {
    setIsOpen(false);
    setWebsiteIsOpen(false);
  }, []);

  useEffect(() => {
    if (!selectedVendor) setIsOpen(false);
    if (!selectedWebsite) setWebsiteIsOpen(false);
  }, [selectedVendor, selectedWebsite]);

  useEffect(() => {
    setFilteredNotifications(
      selectedAccountType
        ? notifications.filter((n) => n.category.toLowerCase() === selectedAccountType.toLowerCase())
        : notifications
    );
    setShowEmptyMessage(filteredNotifications.length === 0 && !isLoading);
  }, [selectedAccountType, notifications, isLoading]);

  useEffect(() => {
    if (isLoading) return;
    setIsLoading(true);
    const fetchData = async () => {
      const fetchedNotifications = await getAllNotifications();
      setNotifications(fetchedNotifications);
      setIsLoading(false);

      const params = new URLSearchParams(window.location.search);
      const siteId = params.get('site');
      const vendorId = params.get('vendor');

      const clearUrlParams = () => {
        const url = window.location.href.split('?')[0];
        window.history.replaceState({}, '', url);
      };

      if (siteId) {
        const websiteData = await getWebsite(siteId);
        setSelectedWebsite(websiteData);
        setSelectedVendor(null);
        setWebsiteIsOpen(true);
        setIsOpen(false);
        clearUrlParams();
      } else if (vendorId) {
        const vendorData = await getVendor(vendorId);
        setSelectedVendor(vendorData);
        setSelectedWebsite(null);
        setIsOpen(true);
        setWebsiteIsOpen(false);
        clearUrlParams();
      }
    };
    fetchData();
  }, [getAllNotifications, refreshKey, getWebsite, getVendor]);

  const paginatedNotifications = filteredNotifications.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getPageRange = () => {
    const halfMaxPages = Math.floor(maxPagesToShow / 2);
    let startPage = Math.max(1, currentPage - halfMaxPages);
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage === totalPages) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    return Array.from({length: endPage - startPage + 1}, (_, index) => startPage + index);
  };

  const pageRange = getPageRange();

  return (
    <div className="flex justify-center min-h-screen bg-white dark:bg-stone-900">
      <div className="w-full flex flex-col">
        <div className="header w-full flex flex-col justify-between bg-gray-400 p-2 dark:bg-stone-800 md:flex-row">
          <div className="search-section mb-2 flex items-center md:mb-0">
            <label
              htmlFor="category"
              className="mr-2 select-none text-sm font-semibold text-gray-100"
            >
              Filter by:
            </label>
            <select
              name="category"
              value={selectedAccountType || ''}
              onChange={(e) => handleFilter(e.target.value || '')}
              className="search-input h-7 rounded-md border-2 border-gray-300 bg-white px-5 pr-10 text-sm focus:outline-none dark:border-stone-700 dark:bg-stone-700 dark:text-gray-300"
            >
              <option value="">All</option>
              {categories.map((category) => (
                <option key={category.id} value={category.category}>
                  {category.name}
                </option>
              ))}
            </select>
            <div className="summary ml-4 flex">
              <button
                onClick={dismissSelected}
                className="new-website-btn mr-3 rounded bg-red-500 px-2 font-semibold text-white hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-700"
                disabled={selectedNotifications.length === 0}
                style={{display: selectedNotifications.length > 0 ? 'block' : 'none'}}
              >
                Clear Selected
                <span className="ml-1 align-text-top text-xs">({selectedNotifications.length})</span>
              </button>
              <button
                onClick={refresh}
                className="new-website-btn mr-3 flex items-center rounded bg-gray-500 px-2 font-semibold text-white hover:bg-gray-600"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="mr-1 h-3 w-3"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
                  />
                </svg>
                Refresh
              </button>
            </div>
          </div>
          <h1
            className="flex select-none justify-center text-lg font-semibold text-gray-200 dark:text-gray-200 md:text-xl">
            Notifications
          </h1>
          <div className="summary mr-2 flex justify-end">
            <div className="group relative">
              <span className="summary-label select-none text-sm font-semibold text-gray-100">
                Total notifications: {filteredNotifications.length.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
        <div className="table-container flex-1 flex flex-col overflow-auto">
          {filteredNotifications.length > 0 ? (
            <>
              <div className="overflow-y-scroll max-h-[78vh] flex-1">
                <table className="data-table w-full table-fixed divide-y divide-gray-200">
                  <thead className="table-header z-9 sticky top-0 rounded-lg bg-gray-500 dark:bg-stone-600 shadow-md">
                  <tr>
                    <th
                      scope="col"
                      className="w-1/12 cursor-pointer select-none rounded-sm px-4 py-2 text-left text-xs font-medium uppercase text-white"
                      onClick={handleSelectAll}
                    >
                      <button
                        className="flex items-center rounded bg-gray-500 px-2 font-semibold text-white hover:bg-gray-600">
                          <span style={{textDecoration: selectAll ? 'underline' : 'none'}}>
                            Select All
                          </span>
                      </button>
                    </th>
                    <th className="w-2/12 px-4 py-1.5 text-left text-xs font-medium uppercase text-white">
                      Category
                    </th>
                    <th className="w-5/12 px-4 py-1.5 text-left text-xs font-medium uppercase text-white">
                      Content
                    </th>
                    <th className="w-2/12 px-4 py-1.5 text-left text-xs font-medium uppercase text-white">
                      Date
                    </th>
                    <th className="w-2/12 px-4 py-1.5 text-left text-xs font-medium uppercase text-white">
                      Actions
                    </th>
                  </tr>
                  </thead>
                  <tbody
                    className="select-none divide-y divide-gray-200 bg-white dark:divide-stone-700 dark:bg-stone-800 dark:text-gray-300">
                  {paginatedNotifications.map((notification) => (
                    <tr key={notification.rowKey} className="hover:bg-gray-100 dark:hover:bg-stone-900">
                      <td className="cell whitespace-nowrap pl-6 py-2 text-xs">
                        <input
                          type="checkbox"
                          checked={selectedNotifications.includes(notification.rowKey)}
                          onChange={(e) => handleNotificationSelection(notification.rowKey, e)}
                        />
                      </td>
                      <td className="cell whitespace-nowrap px-3 py-2 text-xs">
                        {capitalizeFirstLetter(notification.category)}
                      </td>
                      <td className="cell truncate whitespace-nowrap px-6 text-xs">
                        {notification.content}
                      </td>
                      <td className="cell whitespace-nowrap px-3 py-2 text-xs">
                        {formatTimestamp(notification.date)}
                      </td>
                      <td className="cell whitespace-nowrap px-3 py-2 text-xs">
                        <button
                          onClick={() => dismissNotification(notification.rowKey)}
                          className="pointer my-2 mr-2 rounded bg-red-500 px-3 py-1 text-white hover:bg-red-700"
                        >
                          Clear
                        </button>
                        <button
                          onClick={() => viewNotification(notification.link)}
                          className="pointer rounded bg-blue-600 px-3 py-1 text-white hover:bg-blue-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                  </tbody>
                </table>
              </div>
              {filteredNotifications.length > itemsPerPage && (
                <div className="mt-2 flex flex-wrap justify-center bg-white dark:bg-stone-900">
                  <button
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className={`mx-1 my-1 rounded px-3 py-1 ${
                      currentPage === 1
                        ? 'bg-gray-300 text-gray-700 dark:bg-stone-500 dark:text-gray-400'
                        : 'bg-blue-500 text-white dark:bg-indigo-500 dark:text-white'
                    }`}
                  >
                    Previous
                  </button>
                  {pageRange.map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`mx-1 my-1 rounded px-3 py-1 ${
                        currentPage === page
                          ? 'bg-blue-500 text-white dark:bg-indigo-500 dark:text-white'
                          : 'bg-stone-300 text-gray-700 dark:bg-stone-500 dark:text-gray-400'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className={`mx-1 my-1 rounded px-3 py-1 ${
                      currentPage === totalPages
                        ? 'bg-stone-300 text-gray-700 dark:bg-stone-300 dark:text-gray-300'
                        : 'bg-blue-500 text-white dark:bg-indigo-500 dark:text-white'
                    }`}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          ) : (
            showEmptyMessage && (
              <div
                className="flex-1 flex items-center justify-center bg-stone-200 text-xl text-gray-600 dark:bg-stone-800 dark:text-gray-300">
                Notifications Empty
              </div>
            )
          )}
        </div>
        {isOpen && (
          <VendorModal
            isOpen={isOpen}
            onClose={closeVendorModal}
            title={vendorName}
            vendor={selectedVendor}
          >
            <VendorForm vendor={selectedVendor} key={selectedVendor ? selectedVendor.id : 'new'}/>
            <VendorFiles vendorId={selectedVendor ? selectedVendor.id : null}/>
          </VendorModal>
        )}
        {websiteIsOpen && (
          <Draggable>
            <WebsiteModal
              isOpen={websiteIsOpen}
              onClose={closeWebsiteModal}
              title={websiteName}
              website={selectedWebsite}
            >
              <WebsiteForm
                vendor={selectedWebsite}
                key={selectedWebsite ? selectedWebsite.id : 'new'}
              />
            </WebsiteModal>
          </Draggable>
        )}
      </div>
    </div>
  );
}
